const RELOAD_GUARD_KEY = 'reflex:preload-error-reload';

/**
 * Self-heal stale-deploy chunk failures. When a deploy rotates content-hashed
 * chunks, an already-loaded shell's lazy `import()` calls 404 and Vite fires
 * `vite:preloadError`. We hard-reload once per session to pick up the fresh
 * shell. The sessionStorage guard prevents a reload loop if the new shell
 * also fails to load assets (CDN issue, partial deploy).
 *
 * We deliberately do NOT call `event.preventDefault()`. Per Vite, swallowing
 * the event with `preventDefault()` resolves the failed `import()` with
 * `undefined`, so each lazy `.then((m) => ({ default: m.<Page> }))` accessor
 * runs before the reload completes and throws a generic
 * `TypeError: Cannot read properties of undefined (reading '<Page>')`. That
 * message isn't matched by the Sentry `ignoreErrors` filter in `main.tsx`,
 * so a self-healing reload left a non-actionable TypeError in Sentry
 * (AGENT-FLOW-WEB-4R / -4Q / -4S) that buried real frontend exceptions.
 * Letting the import reject (no `preventDefault()`) instead surfaces the
 * chunk-load error message (`Failed to fetch dynamically imported module` /
 * `Importing a module script failed`) that `ignoreErrors` already filters,
 * while the reload below still self-heals the stale shell.
 *
 * Returns a cleanup function so tests can detach the listeners between runs.
 */
export function installPreloadErrorRecovery(): () => void {
  const handlePreloadError = (): void => {
    // Not calling event.preventDefault() on purpose — see the doc above.
    // The import must reject so the existing Sentry ignoreErrors filter
    // suppresses the chunk-load noise; the reload self-heals the shell.
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === '1') return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
    window.location.reload();
  };

  const handleLoad = (): void => {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  };

  window.addEventListener('vite:preloadError', handlePreloadError);
  window.addEventListener('load', handleLoad);

  return () => {
    window.removeEventListener('vite:preloadError', handlePreloadError);
    window.removeEventListener('load', handleLoad);
  };
}
